import React, { useEffect, useMemo, useState, version } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useLearningContinuum from '../../../../../hooks/learning-continuum/useLearningContinuum'
import Module from '../../../../layout/Module'
import { useNavigate } from 'react-router-dom'
import DataGrid from '../../../../table/DataGrid'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import MenuButton from '../../../../button/MenuButton'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import { updateLearningContinuaPreview } from '../../../../../store/actions/learning-continuum.actions'
import Preview from '../Preview/Preview'
import DialogAction from '../Dialog/Dialog'
import { useParams } from 'react-router-dom'
import TextLog from '../../../../text/TextLog'
import TextNote from '../../../../text/TextNote'
import { Checkbox, Chip, Menu, Typography } from '@mui/material'
import ListMenu from '../../../../commom/ListMenu'
import { updateColumnTableCurriculum } from '../../../../../store/actions/datatable.actions'
import FilterButtonLearningContinuum from '../Filter/Filter'
import isEmpty from 'lodash/isEmpty'
import PreviewFilter from '../PreviewFilter/PreviewFilter'
import { columnsLearningContinuum } from '../../utils'
import { renderElementsTableLC } from '../../utils.jsx'
import DialogExport from '../Export/Export.jsx'
import DialogImport from '../Import/Import.jsx'

const List = () => {
  const navigate = useNavigate()
  const { curriculumId } = useParams()
  const dispatch = useDispatch()
  const permissions = useSelector((state) => state?.session?.permissions?.lc || {})
  const preview = useSelector((state) => state?.lc?.preview || null)
  const featureFlag = useSelector((state) => state?.tenant?.featureFlag || {})
  const { isLoading, isLoadingList, getLearningContinuum } = useLearningContinuum()

  const list = useSelector((state) => (state?.lc ? state?.lc[curriculumId]?.list || [] : []))
  const totalList = useSelector((state) => (state?.lc ? state?.lc[curriculumId]?.totalList || 0 : 0))

  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {})
  const state = useSelector((state) => state?.filter?.lc)
  const skip = useSelector((state) => state?.datatable?.lc?.skip || 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.lc?.rowsPerPage || 50)
  const columnsTable = useSelector((state) => state?.datatable?.lc?.[curriculumId] || [])

  const [showDialog, setShowDialog] = useState(null)
  const [show, setShow] = useState(false)
  const [menuEdit, setMenuEdit] = useState({ mouseX: null, mouseY: null })
  const [dataMenu, setDataMenu] = useState(null)
  const [checkIds, setCheckIds] = useState([])

  const onSelectedAll = () => {
    if (checkIds.length === 0) {
      setCheckIds(list.map((row) => row._id))
    } else {
      setCheckIds([])
    }
  }
  const onSelected = (id, checked) => {
    if (checked) {
      setCheckIds([...checkIds, id])
    } else {
      setCheckIds(checkIds.filter((item) => item !== id))
    }
  }
  // Clear the selection and refetch so the list + total stay accurate after a bulk delete.
  const onReset = () => {
    setShowDialog(null)
    setCheckIds([])
    getLearningContinuum(curriculumId, skip, rowsPerPage, state?.filter)
  }

  const options = [
    {
      label: 'Batch Delete',
      icon: 'trash-alt',
      permissions: permissions?.['batch-actions']?.includes('delete'),
      onClick: () => setShowDialog({ type: 'batch-delete', form: { _id: checkIds } })
    }
  ].filter((item) => item.permissions)

  useEffect(() => {
    getLearningContinuum(curriculumId, skip, rowsPerPage, state?.filter)
  }, [curriculumId, rowsPerPage, skip, state, state?.filter])

  useEffect(() => {
    const columns = columnsLearningContinuum(currentCurriculum, list)
    dispatch(updateColumnTableCurriculum(currentCurriculum._id, 'lc', columns, true))
  }, [currentCurriculum])
  const onRedirect = (id, step) => {
    navigate(`/dashboard/learning-continuum/${curriculumId}/edit/${id}/step/${step}`)
  }
  const handleMenuClose = () => setMenuEdit({ mouseX: null, mouseY: null })
  const handleMenuOpen = (event, data) => {
    setMenuEdit({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4
    })
    setDataMenu(data)
  }

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary justify-between'>
        <FilterButtonLearningContinuum />
        <div className='flex items-center justify-end gap-2'>
          {checkIds.length > 1 &&
            options.length > 0 &&
            (options.length > 1 ? (
              <MenuButton title='Actions' icon='ellipsis-v' color='primary' items={options} />
            ) : (
              <ButtonStartIcon
                startIcon={options[0]?.icon || 'cog'}
                color='primary'
                variant='contained'
                disabled={false}
                onClick={options[0]?.onClick}
              >
                {options[0]?.label || 'Action'}
              </ButtonStartIcon>
            ))}
          {isEmpty(
            Object.keys(state?.filter || {})
              .filter((key) => state?.filter[key] !== '')
              .filter((key) => !isEmpty(state?.filter[key] || {}))
          ) || totalList === 0 ? (
            <div className='px-4' />
          ) : (
            <IconTooltipButton
              disabled={isLoading}
              title='Preview'
              icon='expand-alt'
              style={{
                borderRadius: '0.5rem',
                backgroundColor: 'var(--primary)',
                color: 'var(--primary-foreground)',
                padding: '0.45rem'
              }}
              className='shadow-sm'
              onClick={() => setShow(true)}
            />
          )}
          {permissions.export && !isLoadingList && <DialogExport />}
          {permissions.import && !isLoadingList && <DialogImport />}
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer>
        <DataGrid
          id='learning-continuum'
          nameModule='lc'
          curriculumId={curriculumId}
          rows={list.map((row, index) => {
            const elements = renderElementsTableLC(row, currentCurriculum, 'lc')
            const sortValues = {}
            Object.keys(row.lcElements || {}).forEach((k) => {
              sortValues[`_sort_${k}`] = row.lcElements[k] || ''
            })
            return {
              ...row,
              ...sortValues,
              index: index + 1 + rowsPerPage * skip,
              note: <TextNote notes={row.notes || ''} />,
              log: <TextLog updatedAt={row.updatedAt} updatedBy={row.updatedBy} />,
              version: (
                <Chip label={`v${row.version || '1'}`} size='small' color='primary' className='font-bold' />
              ),
              ...elements,
              actions: (
                <div className='flex items-center gap-1 items-center justify-center'>
                  {permissions?.preview && (
                    <IconTooltipButton
                      id={`preview-${row._id}`}
                      disabled={isLoading}
                      title='Preview'
                      icon='expand-alt'
                      onClick={() => dispatch(updateLearningContinuaPreview(row._id))}
                    />
                  )}
                  {permissions?.cloned && (
                    <IconTooltipButton
                      id={`clone-${row._id}`}
                      disabled={isLoading}
                      title='Clone'
                      icon='clone'
                      onClick={() => setShowDialog({ type: 'clone', form: row })}
                    />
                  )}
                  {permissions?.updated && (
                    <IconTooltipButton
                      id={`edit-${row._id}`}
                      disabled={isLoading}
                      title='Edit'
                      icon='pen'
                      onClick={(e) => handleMenuOpen(e, row)}
                    />
                  )}
                  {permissions.archived && (
                    <IconTooltipButton
                      id={`archive-${row._id}`}
                      disabled={isLoading}
                      title='Archive'
                      icon='archive'
                      onClick={() => setShowDialog({ type: 'archive', form: row })}
                    />
                  )}
                  {permissions.deleted && (
                    <IconTooltipButton
                      id={`delete-${row._id}`}
                      disabled={isLoading}
                      title='Delete'
                      icon='trash-alt'
                      onClick={() => setShowDialog({ type: 'delete', form: row })}
                    />
                  )}
                </div>
              ),
              checkIds: (
                <Checkbox
                  id={`checkbox-id-lc-${row._id}`}
                  checked={checkIds.includes(row._id)}
                  onChange={(e) => onSelected(row._id, e.target.checked)}
                  color='primary'
                />
              )
            }
          })}
          loading={isLoadingList}
          totalRows={totalList}
          skip={skip}
          rowsPerPage={rowsPerPage}
          actionsSetting={
            checkIds.length > 0 && (
              <Typography className='min-w-100px' align='right'>
                Rows selected {checkIds.length}
              </Typography>
            )
          }
          columns={[
            { label: '#', key: 'index', align: 'center', width: 10, show: true, hide: false },
            {
              show: permissions['batch-actions'],
              key: 'checkIds',
              align: 'center',
              width: 15,
              label: (
                <Checkbox
                  id={`checkbox-id-lc-header`}
                  checked={checkIds.length > 0 && checkIds.length === list.length}
                  indeterminate={checkIds.length > 0 && checkIds.length < list.length}
                  onChange={(e) => onSelectedAll(e.target.checked)}
                  color='primary'
                />
              )
            },
            ...columnsTable,
            {
              label: 'Note',
              key: 'note',
              align: 'center',
              className: 'box-center',
              show: true,
              width: 50,
              hide: false
            },
            {
              label: 'Log',
              key: 'log',
              align: 'center',
              className: 'text-center box-center',
              width: 120,
              show: true,
              hide: false
            },
            { label: 'Actions', key: 'actions', align: 'center', width: 50, show: true, hide: false }
          ]}
          labelNoRows="You don't have any learning continuum yet"
        />
      </Module.BodyContainer>
      {preview && <Preview />}
      {showDialog && (
        <DialogAction
          show={!!showDialog}
          onClose={() => setShowDialog(null)}
          type={showDialog.type}
          formData={showDialog.form}
          onReset={onReset}
        />
      )}
      {menuEdit && (
        <Menu
          id={`menuEdit-${curriculumId}`}
          MenuListProps={{
            style: { width: '260px' },
            className: 'list-menuEdit p-0'
          }}
          keepMounted
          open={menuEdit.mouseY !== null}
          onClose={handleMenuClose}
          anchorReference='anchorPosition'
          anchorPosition={
            menuEdit.mouseY !== null && menuEdit.mouseX !== null
              ? { top: menuEdit.mouseY, left: menuEdit.mouseX }
              : undefined
          }
        >
          <ListMenu
            id={`menuEdit-list-${curriculumId}`}
            items={[
              {
                label: 'Edit curriculum elements',
                permissions: permissions?.updated?.includes('curriculum-elements'),
                onClick: () => onRedirect(dataMenu._id, 'curriculum-elements')
              },
              {
                label: 'Edit elements',
                permissions: permissions?.updated?.includes('lc-elements'),
                onClick: () => onRedirect(dataMenu._id, 'lc-elements')
              },
              {
                label: 'Edit levels',
                permissions: permissions?.updated?.includes('levels'),
                onClick: () => onRedirect(dataMenu._id, 'levels')
              },
              {
                label: 'Edit levels elements',
                permissions:
                  featureFlag.showLCLevelsElements &&
                  (permissions?.updated?.includes('worked-examples') ||
                    permissions?.created?.includes('teaching-suggestion')),
                onClick: () => onRedirect(dataMenu._id, 'levels-elements')
              },
              {
                label: 'Edit notes',
                permissions: permissions?.updated?.includes('notes'),
                onClick: () => setShowDialog({ type: 'edit notes', form: dataMenu })
              }
            ]
              .filter((item) => item.permissions)
              .map((item) => ({
                ...item,
                onClick: () => {
                  if (item.onClick) {
                    item.onClick()
                  }
                  handleMenuClose()
                }
              }))}
          />
        </Menu>
      )}
      {show && <PreviewFilter show={show} onClose={() => setShow(false)} />}
    </>
  )
}

export default List
